import { Button, CircularProgress, TextField, Typography,     IconButton,
    InputAdornment, } from '@mui/material';
import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';

import * as S from './styles';
import UserFirstLoginLayout from '../user-first-login/layout/user-first-login-layout';
import { AuthService } from '../../../services/auth.service';
import { toast } from 'react-toastify';
import { passwordField } from '../../../schemas/password-policy';
import { PasswordRequirements } from '../../../components/password-requirements';

const ResetPasswordScreen: React.FC = () => {
    const [loadingResetPass, setLoadingResetPass] = useState(false);
    const [searchparams] = useSearchParams();
    const resetId = searchparams.get('id');
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();

    const schema =  Yup.object().shape({
        password: passwordField('Please enter your password'),
        confirmPassword: Yup
            .string().oneOf(
                [Yup.ref("password"), null],
                "Passwords must match"
            )
            .required('Please confirm your password')
        });

    const formik = useFormik({
        initialValues: {
            confirmPassword: '',
            password: '',
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            setLoadingResetPass(true);
            await AuthService.resetPassword(resetId!, values.password, (isValid, msg) => {
                setLoadingResetPass(false);
                if (!isValid) {
                    toast(
                        msg,
                        {
                            autoClose: 1500,
                            type: 'error',
                        }
                    );
                    return
                }
                toast(
                    'Password successfully changed!',
                    {
                        autoClose: 1500,
                        type: 'success',
                    },
                );

                navigate('/login');
            });
        }
    });

    const handleShowPassword = () => {
        setShowPassword((show) => !show);
    };

    const { errors, touched, handleSubmit, getFieldProps } = formik;

    if (!resetId) return <Navigate to="/" replace />;

    return (
        <UserFirstLoginLayout>
            <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    <S.ResetContainer>
                        <Typography variant="h5">Redefine password</Typography>
                        <Typography
                            mt="10px"
                            mb="32px"
                            variant="body1"
                            color="grayMedium.main"
                        >
                            Choose a new password to access POM Safe.
                        </Typography>


                        <S.FormGroup>
                            <TextField
                                label="New password"
                                type={showPassword ? "text" : "password"}
                                placeholder="••••••••"
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleShowPassword} edge="end">
                                                <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                error={Boolean(touched.password && errors.password)}
                                helperText={touched.password && errors.password}
                                {...getFieldProps('password')}
                            />
                        </S.FormGroup>

                        <S.FormGroup>
                            <TextField
                                fullWidth
                                label="Repeat new password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleShowPassword} edge="end">
                                                <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                                helperText={touched.confirmPassword && errors.confirmPassword}
                                {...getFieldProps('confirmPassword')}
                            />
                        </S.FormGroup>

                        <PasswordRequirements password={formik.values.password} />

                        <Button
                            size="large"
                            disabled={loadingResetPass}
                            variant="contained"
                            type="submit"
                            fullWidth
                        >
                            {loadingResetPass ? (
                                <CircularProgress size={27} sx={{ color: '#ffffff' }} />
                            ) : (
                                'Confirm new access password'
                            )}
                        </Button>
                    </S.ResetContainer>
                </Form>
            </FormikProvider>
         
        </UserFirstLoginLayout>
    );
};

export default ResetPasswordScreen;
